import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom';
import { DataGrid, GridCsvExportMenuItem, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExportContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import { CheckAccessFunc } from '../../../../Components/CommonFunctions/CommonFunctions';
import { CommonPatientCard, CommonPatientDetails, ConsultantPatientCard } from '../../../../Components/Common Components/CommonComponents';
import { Colors } from '../../../../Styles/Colors';
import ToastMsg from '../../../../Components/ToastMsg/ToastMsg';
import { getCachevalue } from '../../../../Utility/Services/CacheProviderService';
import { Serviceurls } from '../../../../Utility/API/Serviceurls';
import RestAPIService from '../../../../Utility/Services/RestAPIService';

const ICON_ONLY_BTN_SX = {
    minWidth: 0,
    px: '0.4vw',
    fontSize: 0,
    '& .MuiButton-startIcon': { mx: 0 },
}

const ConsentToolbar = () => (
    <GridToolbarContainer className='eMed_refdoc_grid' sx={{ flexWrap: 'nowrap' }}>
        <Tooltip placement='top' title='Show/Hide Column' arrow>
            <GridToolbarColumnsButton sx={ICON_ONLY_BTN_SX} />
        </Tooltip>
        <Tooltip placement='top' title='Filters' arrow>
            <GridToolbarFilterButton componentsProps={{ button: { sx: ICON_ONLY_BTN_SX } }} />
        </Tooltip>
        <Tooltip placement='top' title='Table Density' arrow>
            <GridToolbarDensitySelector sx={ICON_ONLY_BTN_SX} />
        </Tooltip>
        <Tooltip placement='top' title='Export' arrow>
            <GridToolbarExportContainer sx={ICON_ONLY_BTN_SX}>
                <GridCsvExportMenuItem />
            </GridToolbarExportContainer>
        </Tooltip>
        <GridToolbarQuickFilter placeholder='Search' />
    </GridToolbarContainer>
)

export default function IPConsentForms() {
    const history = useHistory();
    const [states, setStates] = useState({
        consentData: [],
        errorMsg: false, errorMsgText: '',
        successMsg: false, successMsgText: '',
        newPage: 0, pageSize: 10,
        patientDetails: {},
        isloader: false,
        previewOpen: false,
        previewLoader: false,
        previewSrcDoc: '',
        previewUrl: '',
        previewTitle: '',
    })
    const commonDisable = CheckAccessFunc('front_office', 'In Patients', 'Consent Forms', null, 'Tab')?.editAccess

    const errorMessage = (msg) => setStates(prev => ({ ...prev, errorMsg: true, errorMsgText: msg }))

    useEffect(() => {
        try {
            const patientData = JSON.parse(getCachevalue('IppatientData') || '{}')
            setStates(prev => ({ ...prev, patientDetails: patientData }))
            loadInstances(patientData)
        } catch {
            // bad cache; render empty
        }
    }, [])

    const loadInstances = async (patientData) => {
        const ipAdmissionId = patientData?.id || patientData?.ip_admission_id
        if (!ipAdmissionId) return;
        try {
            setStates(prev => ({ ...prev, isloader: true }))
            const response = await RestAPIService.getAll(`${Serviceurls.CONSENT_FORM_LIST}?ip_admission_id=${ipAdmissionId}`)
            const rows = (response?.data?.data || []).map((r, i) => ({ ...r, sno: i + 1 }))
            setStates(prev => ({ ...prev, consentData: rows, isloader: false }))
        } catch (e) {
            setStates(prev => ({ ...prev, isloader: false }))
            errorMessage(e?.response?.data?.message || e.message)
        }
    }

    const buildPdfFilename = (templateName, isoDate) => {
        const safe = (templateName || 'Consent_Form').replace(/[^\w\s-]+/g, '').trim().replace(/\s+/g, '_').slice(0, 80) || 'Consent_Form'
        if (!isoDate) return `${safe}.pdf`
        const d = new Date(isoDate)
        if (isNaN(d.getTime())) return `${safe}.pdf`
        const dd = String(d.getDate()).padStart(2, '0')
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        return `${safe}_${dd}-${mm}-${d.getFullYear()}.pdf`
    }

    const escapeHtmlAttr = (s) => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))

    const buildPreviewSrcDoc = (url, filename) => {
        const safe = escapeHtmlAttr(filename)
        return `<!doctype html><html><head><meta charset="utf-8"/><title>${safe}</title></head><body style="margin:0;padding:0"><iframe src="${url}#toolbar=1" style="position:fixed;inset:0;width:100%;height:100%;border:0" title="${safe}"></iframe></body></html>`
    }

    const fetchPdfBlob = async (instance) => {
        const response = await RestAPIService.PrintPDF(`${Serviceurls.CONSENT_FORM_PDF}${instance.id}/pdf/`)
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const filename = response?.headers?.['x-consent-filename']
            || buildPdfFilename(instance.template_name, instance.issued_at)
        return { blob, filename }
    }

    const openPreview = async (instance) => {
        // Open the dialog immediately with a spinner so the user gets instant
        // feedback; replace the iframe content once the PDF blob arrives.
        setStates(prev => ({
            ...prev,
            previewOpen: true,
            previewLoader: true,
            previewSrcDoc: '',
            previewUrl: '',
            previewTitle: instance.template_name || 'Consent Form',
        }))
        try {
            const { blob, filename } = await fetchPdfBlob(instance)
            const url = URL.createObjectURL(blob)
            setStates(prev => {
                if (prev.previewUrl) URL.revokeObjectURL(prev.previewUrl)
                return {
                    ...prev,
                    previewLoader: false,
                    previewUrl: url,
                    previewSrcDoc: buildPreviewSrcDoc(url, filename),
                }
            })
        } catch (e) {
            setStates(prev => ({ ...prev, previewLoader: false, previewOpen: false }))
            errorMessage(e?.response?.data?.message || e.message)
        }
    }

    const closePreview = () => {
        setStates(prev => {
            if (prev.previewUrl) URL.revokeObjectURL(prev.previewUrl)
            return { ...prev, previewOpen: false, previewLoader: false, previewUrl: '', previewSrcDoc: '', previewTitle: '' }
        })
    }

    const downloadPdf = async (instance) => {
        try {
            const { blob, filename } = await fetchPdfBlob(instance)
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = filename
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            // Defer revocation so Safari/Firefox can flush the download stream.
            setTimeout(() => URL.revokeObjectURL(url), 4000)
        } catch (e) {
            errorMessage(e?.response?.data?.message || e.message)
        }
    }

    const formatDate = (iso) => {
        if (!iso) return '-'
        const d = new Date(iso)
        const dd = String(d.getDate()).padStart(2, '0')
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const yyyy = d.getFullYear()
        let hours = d.getHours()
        const minutes = String(d.getMinutes()).padStart(2, '0')
        const ampm = hours >= 12 ? 'PM' : 'AM'
        hours = String(hours % 12 || 12).padStart(2, '0')
        return `${dd}-${mm}-${yyyy} ${hours}:${minutes} ${ampm}`
    }

    const attender = {
        lineOne: 'Attender Name',
        lineTwo: `${states?.patientDetails?.attender_name || '-'}`,
        lineFour: 'Contact Number',
        LineFive: `${states?.patientDetails?.attender_mobile || '-'}`
    }
    const consultant = {
        lineOne: 'Consultant Name',
        lineTwo: `${states?.patientDetails?.doctor_name || '-'}`,
    }

    const tableView = () => {
        const columns = [
            {
                field: 'sno', headerName: 'S.No', flex: 0.06, sortable: false, filterable: false,
                renderCell: (params) => params.row.sno
            },
            {
                field: 'issued_at', headerName: 'Issued On', flex: 0.22,
                // valueGetter sets the column's underlying value to the
                // pre-formatted dd-mm-yyyy hh:mm AM/PM string, so CSV export
                // serialises the same 12-hour text the user sees in the cell
                // (instead of the raw ISO timestamp that Excel auto-reformats
                // to its locale's 24-hour default).
                valueGetter: (params) => formatDate(params.row.issued_at),
            },
            {
                field: 'template_name', headerName: 'Consent Form Name', flex: 0.40,
                renderCell: (params) => (params.row.template_name?.length > 35
                    ? <Tooltip placement="top" title={params.row.template_name}>
                        <Typography fontSize={'0.9vw'}>{params.row.template_name?.slice(0, 35) + '...'}</Typography>
                      </Tooltip>
                    : <Typography fontSize={'0.9vw'}>{params.row.template_name || '-'}</Typography>)
            },
            {
                field: 'issued_by', headerName: 'Issued By', flex: 0.20,
                renderCell: (params) => params.row.issued_by || '-'
            },
            {
                field: 'action', headerName: 'Action', flex: 0.12, sortable: false, filterable: false,
                renderCell: (params) => (
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip placement="top" title="View" arrow>
                            <IconButton size="small" onClick={() => openPreview(params.row)}>
                                <VisibilityIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip placement="top" title="Download" arrow>
                            <IconButton size="small" onClick={() => downloadPdf(params.row)}>
                                <DownloadIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                )
            },
        ]
        return (
            <Box sx={{ height: '70vh', marginTop: '.5vw' }}>
                <DataGrid
                    rows={states.consentData}
                    columns={columns}
                    getRowId={(row) => row.id}
                    page={states?.newPage}
                    pageSize={states?.pageSize}
                    components={{
                        Toolbar: ConsentToolbar,
                        NoRowsOverlay: () => (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                {'No Records To Be Shown'}
                            </Box>
                        )
                    }}
                    onPageChange={(newPage) => setStates(prev => ({ ...prev, newPage }))}
                    onPageSizeChange={(newPageSize) => setStates(prev => ({ ...prev, pageSize: newPageSize }))}
                    rowsPerPageOptions={[10, 20, 30]}
                    pagination
                    headerHeight={40}
                    density='compact'
                    disableSelectionOnClick
                    loading={states?.isloader}
                />
            </Box>
        )
    }

    const msgClose = () => setStates(prev => ({ ...prev, errorMsg: false, errorMsgText: '', successMsg: false, successMsgText: '' }))

    return (
        <Box sx={{ height: '79.5vh', width: '94vw', backgroundColor: Colors.Background, padding: '.5vw' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Box className='eMed_Patient_Details_Long eMed_patient_box' emed_tid="surgery_notes_patient_details">
                        <CommonPatientDetails data={states?.patientDetails} showDetailed={true} emed_tid="consent_form_patient_details_component" />
                    </Box>
                    <CommonPatientCard details={attender} showDetailed={true} emed_tid="consent_form_attender_card" />
                    <ConsultantPatientCard name={consultant} emed_tid="consent_form_consultant_card" />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Button size='small' variant='contained' disabled={!commonDisable}
                        sx={{ textTransform: 'capitalize', width: '12vw', mt: '0.5vw', mb: '0.5vw' }}
                        onClick={() => history.push('/IPHome/PatientDetails/ConsentForms/Add')}>
                        Issue Consent Form
                    </Button>
                </Box>
            </Box>
            <Box sx={{ height: '68vh', width: '93vw', marginTop: '.5vw', backgroundColor: 'white' }}>
                {tableView()}
            </Box>
            {states?.errorMsg && <ToastMsg severity={'error'} msg={states?.errorMsgText} msgPop={msgClose} />}
            {states?.successMsg && <ToastMsg severity={'success'} msg={states?.successMsgText} msgPop={msgClose} />}
            <Dialog open={!!states.previewOpen} onClose={closePreview} maxWidth="md" fullWidth
                PaperProps={{ sx: { height: '85vh' } }}>
                <DialogTitle sx={{ m: 0, p: '0.5vw 1vw', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography fontSize={'1vw'} fontWeight={600}>{states.previewTitle || 'Consent Form'}</Typography>
                    <IconButton size="small" onClick={closePreview}><CloseIcon fontSize="small" /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {states.previewLoader
                        ? <CircularProgress color='primary' size={'2vw'} />
                        : states.previewSrcDoc
                            ? <iframe style={{ width: '100%', height: '100%', border: 'none' }}
                                srcDoc={states.previewSrcDoc} title={states.previewTitle || 'Consent PDF'} />
                            : <Typography fontSize={'0.9vw'} color="text.secondary">Preview unavailable</Typography>}
                </DialogContent>
            </Dialog>
        </Box>
    )
}
